import { IAplosFileApiService } from "@/components/FileManager/services/AplosFileApiService";
import { GetAplosFileArgs } from "@/components/FileManager/models/GetAplosFileArgs";
import { InjectableService } from "@/shared/services/InjectableService";
import { GetExecutionStatusArgs } from "@/shared/Executions/models/GetExecutionStatusArgs";
import { GetExecutionConfigArgs } from "@/shared/Executions/models/GetExecutionConfigArgs";
import { IExecutionApiService } from "@/shared/Executions/services/ExecutionApiService";
import { AdvancedAnalysisWizardSharedModel } from "../models/AdvancedAnaylsisWizardSharedModel";
import { AdvancedAnalysisWizardInitializationArgs } from "../models/AdvancedAnalysisWizardInitializationArgs";
import { AplosFile } from "@/components/FileManager/models/AplosFile";
import { AplosFileLineage } from "@/components/FileManager/models/AplosFileLineage";
import { GetRulesArgs } from "@/components/DataCleaning/models/GetRulesArgs";
import { IDataCleaningApiService } from "@/components/DataCleaning/services/DataCleaningApiService";
import { CdnSchemaFactory } from "@/shared/CdnSchema/services/CdnSchemaFactory";
import { DataCleaningJsonParsingService } from "@/components/DataCleaning/services/DataCleaningJsonParsingService";
import { AnalysisConfigJsonParsingService } from "@/components/AnalysisConfig/services/AnalysisConfigJsonParsingService";
import { CalculationsJsonParsingService } from "@/components/CustomCalculations/services/CalculationsJsonParsingService";
import { TablesJsonParsingService } from "@/components/Tables/services/TablesJsonParsingService";
import { PlottingJsonParsingService } from "@/components/Plotting/services/PlottingJsonParsingService";
import { CustomReportsJsonParsingService } from "@/components/CustomReports/services/CustomReportsJsonParsingService";
import { CdnSchemaType } from "@/shared/CdnSchema/models/CdnSchemaType";
import { ICustomSchemaStore } from "@/shared/models/ICustomSchemaStore";
import { AdvancedAnalysisService } from "@/components/AdvancedAnalysis/services/AdvancedAnalysisService";
import { SchemaService } from "@/shared/services/SchemaService";
import { AdvancedAnalysisField } from "@/components/AdvancedAnalysis/models/AdvancedAnalysisField";
import { JsonEditorSchemaType } from "@/shared/JsonEditorSchema/models/JsonEditorCdnSchemaType";
import { AdvancedAnalysis } from "@/components/AdvancedAnalysis/models/AdvancedAnalysis";
import { AnalysisConfigField } from "@/components/AnalysisConfig/models/AnalysisConfigField";
import { CalculationField } from "@/components/CustomCalculations/models/CalculationField";
import { TableField } from "@/components/Tables/models/TableField";
import { PlotField } from "@/components/Plotting/models/PlotField";
import { CustomReportField } from "@/components/CustomReports/models/CustomReportField";



export class AdvancedAnalysisWizardInitializationService extends InjectableService {

    static readonly injectableKey = 'AdvancedAnalysisWizardInitializationService';

    get injectableKey() {
        return (this.constructor as typeof AdvancedAnalysisWizardInitializationService).injectableKey;
    }

    constructor(private readonly _aplosFileApiService: IAplosFileApiService,
        private readonly _executionApiService: IExecutionApiService,
        private readonly _aplosDataCleaningApiService: IDataCleaningApiService,
        private readonly _cdnSchemaService: CdnSchemaFactory,
        private readonly _dataCleaningJsonParsingService: DataCleaningJsonParsingService,
        private readonly _analysisConfigJsonParsingService: AnalysisConfigJsonParsingService,
        private readonly _calculationsJsonParsingService: CalculationsJsonParsingService,
        private readonly _tablesJsonParsingService: TablesJsonParsingService,
        private readonly _plottingJsonParsingService: PlottingJsonParsingService,
        private readonly _customReportsJsonParsingService: CustomReportsJsonParsingService
    ) {
        super();
    }

    public async intializeSharedModelFromArgs(tenantId: string, userId: string, sharedModel: AdvancedAnalysisWizardSharedModel,
        initializationArgs: AdvancedAnalysisWizardInitializationArgs, customSchemaStore: ICustomSchemaStore): Promise<AdvancedAnalysisWizardSharedModel> {

        if (tenantId == null) throw Error("tenantId is required");
        if (userId == null) throw Error("userId is required");
        if (sharedModel == null) throw Error("sharedModel is required");
        if (initializationArgs == null) throw Error("initializationArgs is required");


        // Collect promises as Promise<void> so different API return types can be handled
        // inline and mapped into the sharedModel before Promise.all resolves.
        const promises: Array<Promise<void>> = [];

        let newAdvancedAnalysis: AdvancedAnalysis | undefined = undefined;
        let analysisFile: AplosFile | undefined = undefined;
        if (initializationArgs.analysisFileId != null) {

            const args = new GetAplosFileArgs({
                tenantId: tenantId as string,
                userId: userId as string,
                fileId: initializationArgs.analysisFileId!
            });
            const response = await this._aplosFileApiService.getFile(args);
            analysisFile = response.data as AplosFile;
        }

        // WithAdvancedAnalysisExecutionId branch
        if (initializationArgs.advancedAnalysisExecutionId) {

            sharedModel.inputFile = analysisFile;

            promises.push(
                (async () => {
                    const args = new GetExecutionConfigArgs({
                        userId: userId as string,
                        tenantId: tenantId as string,
                        executionId: initializationArgs.advancedAnalysisExecutionId
                    });

                    const executionConfigResponse = await this._executionApiService.getExecutionConfig(args);
                    sharedModel.advancedAnalysis = executionConfigResponse.data;
                })()
            )

            promises.push(
                (async () => {
                    const args = new GetExecutionStatusArgs({
                        userId: userId as string,
                        tenantId: tenantId as string,
                        executionId: initializationArgs.advancedAnalysisExecutionId!
                    });

                    const getExecutionStatusResponse = await this._executionApiService.getExecutionStatus(args);
                    //TODO: replicate execution for ALL steps fomr single
                    sharedModel.advancedAnalysisExecution = getExecutionStatusResponse.data;
                })()
            )
        }
        // WithWizardExecutionIds branch
        else {

            const availableAdvancedAnalysisSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(
                CdnSchemaType.AdvancedAnalysis,
                customSchemaStore
            );

            const firstSchemaType = availableAdvancedAnalysisSchemas?.length
                ? availableAdvancedAnalysisSchemas[0]
                : null;

            if (firstSchemaType == null) throw Error('Missing required Advanced Analysis CDN Schema content!');

            newAdvancedAnalysis = AdvancedAnalysisService.createAdvancedAnalysisFromAdvancedAnalysisSchema(firstSchemaType)

            newAdvancedAnalysis.configuration =
                AdvancedAnalysisService.initializeAdvancedAnalysisConfiguration(
                    newAdvancedAnalysis,
                    firstSchemaType!.fields ?? []
                )

            let cleanedFileId: string | undefined = undefined;
            let dataCleaningOutputFileName: string | undefined = "";
            // Get original file for cleaned file an dset as input
            // set cleanedFileId so we can retrieve rules used to generate cleaned data
            if (analysisFile != null) {

                if (analysisFile.lineage === AplosFileLineage.Cleaned) {
                    cleanedFileId = analysisFile.id;
                    dataCleaningOutputFileName = analysisFile.name;
                    const getOriginalFileArgs = new GetAplosFileArgs({ tenantId, userId, fileId: analysisFile.parentId! });
                    const originalInputResponse = await this._aplosFileApiService.getFile(getOriginalFileArgs);
                    sharedModel.inputFile = originalInputResponse.data;
                    //TODO: better way to initialize unknown key?
                    newAdvancedAnalysis.configuration.fileId = originalInputResponse.data.id;
                }
                else {
                    sharedModel.inputFile = analysisFile;
                    newAdvancedAnalysis.configuration.fileId = analysisFile.id;
                }

            }

            if (cleanedFileId) {

                promises.push(
                    (async () => {
                        const args = new GetRulesArgs({
                            tenantId: tenantId as string,
                            userId: userId as string,
                            fileId: cleanedFileId
                        });
                        const flatRules = await this._aplosDataCleaningApiService.getRules(args);
                        const ruleSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.RuleSchema, customSchemaStore);
                        const parseResult = this._dataCleaningJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(flatRules), ruleSchemas);
                        if (!parseResult.valid) throw Error("Error loading and initializing existing rules!");

                        const ruleField = SchemaService.tryGetFirstFieldOfType<AdvancedAnalysisField>(
                            firstSchemaType.fields ?? [],
                            JsonEditorSchemaType.RuleSchema);
                        if (ruleField) {
                            newAdvancedAnalysis.configuration[ruleField.name] = { rules: parseResult.parsedValue, output: { fileName: dataCleaningOutputFileName } };
                        }
                    })()
                )
            }

            if (initializationArgs.analysisExecutionId) {

                promises.push(
                    (async () => {
                        const args = new GetExecutionConfigArgs({
                            userId: userId as string,
                            tenantId: tenantId as string,
                            executionId: initializationArgs.analysisExecutionId!
                        });

                        const executionConfigResponse = await this._executionApiService.getExecutionConfig(args);

                        const analysisConfigSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.AnalysisConfigSchema, customSchemaStore);
                        const parseResult = this._analysisConfigJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(executionConfigResponse.data?.calculation), analysisConfigSchemas);
                        if (!parseResult.valid) throw Error("Error loading and initializing existing PK Analysis config!");

                        const analysisConfigField = SchemaService.tryGetFirstFieldOfType<AnalysisConfigField>(
                            firstSchemaType.fields ?? [],
                            JsonEditorSchemaType.AnalysisConfigSchema);
                        if (analysisConfigField) {
                            //TODO: tie the parsed structures to a specific expected?
                            // or write a transformation service to massage any valid structure to the expected?
                            newAdvancedAnalysis.configuration[analysisConfigField.name] = parseResult.parsedValue;
                        }

                    })()
                )

            }


            if (initializationArgs.customCalculationsExecutionId) {

                promises.push(
                    (async () => {
                        const args = new GetExecutionStatusArgs({
                            userId: userId as string,
                            tenantId: tenantId as string,
                            executionId: initializationArgs.customCalculationsExecutionId!
                        });

                        const customCaculationsConfigResponse = await this._executionApiService.getExecutionConfig(args);

                        const calculationSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.CalculationSchema, customSchemaStore);
                        const parseResult = this._calculationsJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(customCaculationsConfigResponse.data?.customCalculations), calculationSchemas);
                        if (!parseResult.valid) throw Error("Error loading and initializing existing Custom Caluclations config!");

                        const customCalculationsField = SchemaService.tryGetFirstFieldOfType<CalculationField>(
                            firstSchemaType.fields ?? [],
                            JsonEditorSchemaType.CalculationSchema);
                        if (customCalculationsField) {

                            //TODO: transformative service to take parseed results and put into expected shape?
                            const customCalculations = {
                                calculations: parseResult.parsedValue?.calculations,
                                output: {
                                    sort: parseResult.parsedValue?.output?.sort
                                }
                            }
                            // customCalculations.customCalculations?
                            newAdvancedAnalysis.configuration[customCalculationsField.name] = customCalculations;
                        }
                    })()
                )

            }

            if (initializationArgs.postProcessingExecutionId) {

                promises.push(
                    (async () => {
                        const args = new GetExecutionStatusArgs({
                            userId: userId as string,
                            tenantId: tenantId as string,
                            executionId: initializationArgs.postProcessingExecutionId!
                        });

                        const postProcessingConfigResponse = await this._executionApiService.getExecutionConfig(args);

                        const tableSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.TableSchema, customSchemaStore);
                        const plotSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.PlotSchema, customSchemaStore);

                        const tables = postProcessingConfigResponse.data?.customTables?.tables;
                        if (tables) {
                            const tableParseResult = this._tablesJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(postProcessingConfigResponse.data?.customTables?.tables), tableSchemas);
                            if (!tableParseResult.valid) throw Error("Error loading and initializing existing tables post processing config!");
                            const tablesField = SchemaService.tryGetFirstFieldOfType<TableField>(
                                firstSchemaType.fields ?? [],
                                JsonEditorSchemaType.TableSchema);
                            if (tablesField) {
                                //TODO: transformative service to take parseed results and put into expected shape?
                                const customTables = {
                                    tables: tableParseResult.parsedValue,
                                }
                                newAdvancedAnalysis.configuration[tablesField.name] = customTables;
                            }
                        }

                        const plots = postProcessingConfigResponse.data?.customPlots?.plots;
                        if (plots) {
                            const plotParseResult = this._plottingJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(postProcessingConfigResponse.data?.customPlots?.plots), plotSchemas);
                            if (!plotParseResult.valid) throw Error("Error loading and initializing existing plots post processing config!");
                            const plotsField = SchemaService.tryGetFirstFieldOfType<PlotField>(
                                firstSchemaType.fields ?? [],
                                JsonEditorSchemaType.PlotSchema);
                            if (plotsField) {
                                //TODO: transformative service to take parseed results and put into expected shape?
                                const customPlots = {
                                    plots: plotParseResult.parsedValue,
                                }
                                newAdvancedAnalysis.configuration[plotsField.name] = customPlots;
                            }
                        }

                    })()
                )

            }

            if (initializationArgs.customReportsExecutionId) {

                promises.push(
                    (async () => {
                        const args = new GetExecutionStatusArgs({
                            userId: userId as string,
                            tenantId: tenantId as string,
                            executionId: initializationArgs.customReportsExecutionId!
                        });

                        const customReportsConfigResponse = await this._executionApiService.getExecutionConfig(args);

                        const customReportSchemas = await this._cdnSchemaService.loadAvailableSchemasForCdnSchemaType(CdnSchemaType.CustomReportSchema, customSchemaStore);
                        const parseResult = this._customReportsJsonParsingService.tryParseEntitiesFromJson(JSON.stringify(customReportsConfigResponse.data?.customReports?.reports), customReportSchemas);
                        if (!parseResult.valid) throw Error("Error loading and initializing existing report!");

                        const customReportField = SchemaService.tryGetFirstFieldOfType<CustomReportField>(
                            firstSchemaType.fields ?? [],
                            JsonEditorSchemaType.CustomReportSchema);
                        if (customReportField) {
                            //TODO: transformative service to take parseed results and put into expected shape?
                            const customReports = {
                                reports: parseResult.parsedValue,
                            }
                            newAdvancedAnalysis.configuration[customReportField.name] = customReports;
                        }
                    })()
                )

            }


        }

        await Promise.all(promises);

        if (newAdvancedAnalysis != null) {
            sharedModel.advancedAnalysis = AdvancedAnalysisService.getFlattenedAdvancedAnalysis(newAdvancedAnalysis, newAdvancedAnalysis._type);
        }

        return sharedModel;
    }


}